import React from 'react';
import {
  Users,
  CreditCard,
  AlertTriangle,
  Wallet,
  ArrowRight,
  Printer,
  CheckCircle2,
  TrendingUp,
  Building2,
  Calendar,
  Smartphone,
} from 'lucide-react';
import { TransaksiPembayaran, Siswa, TagihanSiswa } from '../types/index.js';
import { SppTrendChart } from './dashboard/SppTrendChart.js';
import { LowAttendanceWarning } from './dashboard/LowAttendanceWarning.js';
import { formatPaymentMethod, getPokokNominal } from '../utils/paymentMethod.js';

interface DashboardHomeViewProps {
  stats: any;
  transaksiList: TransaksiPembayaran[];
  siswaList: Siswa[];
  tagihanList: TagihanSiswa[];
  onNavigate: (menu: string) => void;
  onShowReceipt: (trx: TransaksiPembayaran) => void;
  onOpenMobile: () => void;
  onSyncRfid?: () => Promise<void>;
  isSyncingRfid?: boolean;
}

export const DashboardHomeView: React.FC<DashboardHomeViewProps> = ({
  stats,
  transaksiList,
  siswaList,
  tagihanList,
  onNavigate,
  onShowReceipt,
  onOpenMobile,
  onSyncRfid,
  isSyncingRfid = false,
}) => {
  const totalSiswa = siswaList.length;
  const totalPenerimaan = transaksiList
    .filter((t) => t.status === 'SUCCESS')
    .reduce((sum, t) => sum + t.totalBayar, 0);

  const totalTunggakan = tagihanList
    .filter((t) => t.status !== 'PAID')
    .reduce((sum, t) => sum + t.sisa, 0);

  const totalTabungan = siswaList.reduce((sum, s) => sum + (s.saldoTabungan || 0), 0);

  return (
    <div className="space-y-5 animate-in fade-in duration-200">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-sky-800 via-sky-700 to-teal-800 rounded-2xl p-6 text-white shadow-sm flex flex-wrap items-center justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="bg-amber-400 text-slate-950 text-[10px] font-extrabold px-2 py-0.5 rounded">
              TAHUN AJARAN 2026/2027
            </span>
            <span className="text-xs text-sky-200">Unit: SDMTPONOROGO</span>
          </div>
          <h1 className="text-xl font-black tracking-tight text-white">
            Sistem Informasi Pembayaran & Loket Kasir SDMT Ponorogo
          </h1>
          <p className="text-xs text-sky-100 max-w-2xl leading-relaxed">
            Aplikasi terintegrasi dengan Nomor Virtual Account Bank Muamalat (BMI), Duitku, dan Midtrans Snap untuk pembayaran SPP, infaq, dan administrasi sekolah.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => onNavigate('Loket Pembayaran')}
            className="flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-slate-950 text-xs font-bold px-4 py-2.5 rounded-xl shadow-sm transition"
          >
            <CreditCard className="w-4 h-4" />
            <span>Buka Loket Kasir</span>
          </button>
        </div>
      </div>

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Siswa */}
        <div
          onClick={() => onNavigate('Siswa')}
          className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs hover:border-sky-400 transition cursor-pointer"
        >
          <div className="flex items-center justify-between text-slate-500">
            <span className="text-xs font-bold uppercase">Siswa Terdaftar</span>
            <div className="p-2 rounded-lg bg-sky-50 text-sky-600">
              <Users className="w-4 h-4" />
            </div>
          </div>
          <p className="text-2xl font-black text-slate-900 mt-2">{totalSiswa} Siswa</p>
          <p className="text-[11px] text-slate-400 mt-1 flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3 text-emerald-500" />
            <span>Semua memiliki nomor Virtual Account</span>
          </p>
        </div>

        {/* Penerimaan */}
        <div
          onClick={() => onNavigate('Report Pembayaran')}
          className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs hover:border-emerald-400 transition cursor-pointer"
        >
          <div className="flex items-center justify-between text-slate-500">
            <span className="text-xs font-bold uppercase">Total Penerimaan Kas</span>
            <div className="p-2 rounded-lg bg-emerald-50 text-emerald-600">
              <TrendingUp className="w-4 h-4" />
            </div>
          </div>
          <p className="text-2xl font-black font-mono text-emerald-700 mt-2">
            Rp {totalPenerimaan.toLocaleString('id-ID')}
          </p>
          <p className="text-[11px] text-slate-400 mt-1">Lunas melalui Loket & Payment Gateway</p>
        </div>

        {/* Tunggakan */}
        <div
          onClick={() => onNavigate('Report Tunggakan')}
          className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs hover:border-rose-400 transition cursor-pointer"
        >
          <div className="flex items-center justify-between text-slate-500">
            <span className="text-xs font-bold uppercase">Total Piutang / Tunggakan</span>
            <div className="p-2 rounded-lg bg-rose-50 text-rose-600">
              <AlertTriangle className="w-4 h-4" />
            </div>
          </div>
          <p className="text-2xl font-black font-mono text-rose-600 mt-2">
            Rp {totalTunggakan.toLocaleString('id-ID')}
          </p>
          <p className="text-[11px] text-rose-500 mt-1 font-medium">Klik untuk kirim notifikasi WA</p>
        </div>

        {/* Tabungan */}
        <div
          onClick={() => onNavigate('Saldo Siswa')}
          className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs hover:border-sky-400 transition cursor-pointer"
        >
          <div className="flex items-center justify-between text-slate-500">
            <span className="text-xs font-bold uppercase">Saldo Tabungan Siswa</span>
            <div className="p-2 rounded-lg bg-indigo-50 text-indigo-600">
              <Wallet className="w-4 h-4" />
            </div>
          </div>
          <p className="text-2xl font-black font-mono text-sky-900 mt-2">
            Rp {totalTabungan.toLocaleString('id-ID')}
          </p>
          <p className="text-[11px] text-slate-400 mt-1">Rekening Bank Muamalat Siswa</p>
        </div>
      </div>

      {/* Warning Banner: Low Attendance Paying Students (Solution X107 Sync) */}
      {/* 
      <LowAttendanceWarning
        siswaList={siswaList}
        transaksiList={transaksiList}
        onNavigate={onNavigate}
        onSyncRfid={onSyncRfid}
        isSyncing={isSyncingRfid}
      /> 
      */}

      {/* Spp Monthly Trend Analytics Chart (Recharts) */}
      <SppTrendChart
        transaksiList={transaksiList}
        tagihanList={tagihanList}
        onNavigate={onNavigate}
      />

      {/* Quick Access Menu Tiles matching document */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-xs">
        <h3 className="text-sm font-bold text-slate-900 mb-3">Akses Cepat Modul Pembayaran</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 text-xs">
          <button
            onClick={() => onNavigate('Loket Pembayaran')}
            className="p-3 rounded-lg border border-slate-200 bg-slate-50/70 hover:bg-sky-50 hover:border-sky-300 text-left transition flex flex-col justify-between h-24"
          >
            <CreditCard className="w-5 h-5 text-sky-600" />
            <div>
              <p className="font-bold text-slate-800">Loket Kasir</p>
              <p className="text-[10px] text-slate-400">Penerimaan & POS</p>
            </div>
          </button>

          <button
            onClick={() => onNavigate('Tagihan Siswa')}
            className="p-3 rounded-lg border border-slate-200 bg-slate-50/70 hover:bg-sky-50 hover:border-sky-300 text-left transition flex flex-col justify-between h-24"
          >
            <Calendar className="w-5 h-5 text-amber-600" />
            <div>
              <p className="font-bold text-slate-800">Tagihan Siswa</p>
              <p className="text-[10px] text-slate-400">Generate & Tarif</p>
            </div>
          </button>

          <button
            onClick={() => onNavigate('Siswa')}
            className="p-3 rounded-lg border border-slate-200 bg-slate-50/70 hover:bg-sky-50 hover:border-sky-300 text-left transition flex flex-col justify-between h-24"
          >
            <Users className="w-5 h-5 text-emerald-600" />
            <div>
              <p className="font-bold text-slate-800">Siswa & VA</p>
              <p className="text-[10px] text-slate-400">Cetak Kartu VA</p>
            </div>
          </button>

          <button
            onClick={() => onNavigate('Pos Tagihan')}
            className="p-3 rounded-lg border border-slate-200 bg-slate-50/70 hover:bg-sky-50 hover:border-sky-300 text-left transition flex flex-col justify-between h-24"
          >
            <Building2 className="w-5 h-5 text-purple-600" />
            <div>
              <p className="font-bold text-slate-800">Pos Tagihan</p>
              <p className="text-[10px] text-slate-400">SPP, Infaq, Seragam</p>
            </div>
          </button>

          <button
            onClick={() => onNavigate('Payment Gateway')}
            className="p-3 rounded-lg border border-slate-200 bg-slate-50/70 hover:bg-sky-50 hover:border-sky-300 text-left transition flex flex-col justify-between h-24"
          >
            <CheckCircle2 className="w-5 h-5 text-teal-600" />
            <div>
              <p className="font-bold text-slate-800">Gateway Console</p>
              <p className="text-[10px] text-slate-400">Duitku & Midtrans</p>
            </div>
          </button>

          <button
            onClick={() => onNavigate('Report Pembayaran')}
            className="p-3 rounded-lg border border-slate-200 bg-slate-50/70 hover:bg-sky-50 hover:border-sky-300 text-left transition flex flex-col justify-between h-24"
          >
            <TrendingUp className="w-5 h-5 text-indigo-600" />
            <div>
              <p className="font-bold text-slate-800">Rekap Kas</p>
              <p className="text-[10px] text-slate-400">Laporan Keuangan</p>
            </div>
          </button>
        </div>
      </div>

      {/* Recent Payments Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-sm text-slate-900">Transaksi Pembayaran Terkini</h3>
            <p className="text-xs text-slate-500">Mutasi kas loket dan notifikasi otomatis payment gateway</p>
          </div>

          <button
            onClick={() => onNavigate('Cetak Bukti & Cancel')}
            className="text-xs font-semibold text-sky-700 hover:text-sky-800 flex items-center gap-1"
          >
            <span>Lihat Semua</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-bold uppercase">
              <tr>
                <th className="py-2.5 px-4">Waktu</th>
                <th className="py-2.5 px-4 font-mono">No. Kuitansi</th>
                <th className="py-2.5 px-4">NIS</th>
                <th className="py-2.5 px-4">Nama Siswa</th>
                <th className="py-2.5 px-4">Kelas</th>
                <th className="py-2.5 px-4 text-right">Nominal (Rp)</th>
                <th className="py-2.5 px-4">Channel</th>
                <th className="py-2.5 px-4 text-center">Kuitansi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {transaksiList.slice(0, 5).map((trx) => (
                <tr key={trx.id} className="hover:bg-sky-50/50 transition">
                  <td className="py-2.5 px-4 text-slate-500">{trx.tanggal.slice(0, 16)}</td>
                  <td className="py-2.5 px-4 font-mono font-bold text-sky-800">{trx.noKuitansi}</td>
                  <td className="py-2.5 px-4 font-mono text-slate-700">{trx.nis}</td>
                  <td className="py-2.5 px-4 font-semibold text-slate-900">{trx.namaSiswa}</td>
                  <td className="py-2.5 px-4 text-slate-600">{trx.kelas}</td>
                  <td className="py-2.5 px-4 text-right font-mono font-bold text-emerald-700">
                    {getPokokNominal(trx).toLocaleString('id-ID')}
                  </td>
                  <td className="py-2.5 px-4">
                    <span className="bg-slate-100 px-2 py-0.5 rounded text-[10px] font-semibold">
                      {formatPaymentMethod(trx.channel, trx.metodeBayar)}
                    </span>
                  </td>
                  <td className="py-2.5 px-4 text-center">
                    <button
                      onClick={() => onShowReceipt(trx)}
                      className="px-2.5 py-1 bg-sky-50 hover:bg-sky-100 text-sky-700 border border-sky-300 rounded font-semibold text-[11px] inline-flex items-center gap-1"
                    >
                      <Printer className="w-3 h-3" />
                      <span>Cetak</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
